// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useGetLinks } from "@awc/App/Store/Selectors/LinksSelector";
import xIconsObject from "@awc/XDL/Icons/xngCommonIcons";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    // XNG-Changes:
    titleLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    if (hasMissingCategories) {
        return null;
    }

    // <XNG-Changes>
    const linkListId = fields.title.replace(/\s/g, "").trim();
    // </XNG-Changes>

    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${styles.linkListWrapper?.css}
        `,
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal"
                ? `
                    display: inline-block;
                    padding: 1px 18px 0 0;

                    &:last-child {
                        padding: 1px 0 0 0;
                    }
                `
                : "padding: 1px 0 0 0;"}
            ${styles.linkWrapper?.css}
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                {(fields.title || titleLink?.url) && (
                    <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                        {titleLink?.url ? (
                            <Link
                                className="list-title"
                                href={titleLink.url}
                                aria-label={fields.title || titleLink.title || "list link"}
                                {...styles.titleLink}
                                // XNG-Changes: add id & tabIndex
                                id={linkListId}
                                tabIndex={0}
                            >
                                {fields.title || titleLink.title}
                            </Link>
                        ) : (
                            <Typography
                                className="list-title"
                                as="h4"
                                {...styles.title}
                                // XNG-Changes: add id & tabIndex
                                id={linkListId}
                                tabIndex={0}
                            >
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                )}
                {/* XNG-Changes: wrap the list items with ul for more accessability improvements */}
                <StyledWrapper as="ul" aria-labelledby={linkListId} {...styles.linksWrapper}>
                    {links.map((link, index) => (
                        <StyledWrapper as="li" className="links-wrapper" key={index} {...directionStyles}>
                            {link.url && (
                                <Link
                                    className="list-link"
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    aria-label={fields.links[index].fields.overriddenTitle || link.title || "list link"}
                                    {...styles.link}
                                    {...{
                                        icon: {
                                            position: (fields.links[index].fields as any).iconPosition || "left",
                                            iconProps: {
                                                src:
                                                    xIconsObject[(fields.links[index].fields as any)["icon"]] ||
                                                    undefined,
                                                size: 20,
                                                css: css`
                                                    width: ${(fields.links[index].fields as any).iconWidth || 20}px;
                                                    height: ${(fields.links[index].fields as any).iconHeight || 20}px;
                                                    svg {
                                                        width: ${(fields.links[index].fields as any).iconWidth || 20}px;
                                                        height: ${(fields.links[index].fields as any).iconHeight ||
                                                        20}px;
                                                    }
                                                `,
                                            },
                                        },
                                    }}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    // XNG-Changes
                    {
                        name: "icon",
                        editorTemplate: "DropDownField",
                        options: Object.keys(xIconsObject).map(iconName => ({
                            displayName: iconName,
                            value: iconName,
                        })),
                        defaultValue: "",
                    },
                    {
                        name: "iconPosition",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Right", value: "right" },
                            { displayName: "Left", value: "left" },
                        ],
                        defaultValue: "left",
                        tab: basicTab,
                    },
                    {
                        name: "iconWidth",
                        displayName: "icon width",
                        editorTemplate: "IntegerField",
                        defaultValue: 20,
                        max: 40,
                        min: 0,
                    },
                    {
                        name: "iconHeight",
                        displayName: "icon height",
                        editorTemplate: "IntegerField",
                        defaultValue: 20,
                        max: 40,
                        min: 0,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
